import { ApiError, ErrorsMap } from "api/http/utils"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import { DateInput } from "components/inputs/DatePicker"
import { LoadingTable } from "./LoadingTable"
import { MediaConfigScope } from "routes/Base/Admin/GlobalMediaTypes/types"
import { NEGOTIATION_TYPES, RateCardDetails } from "models/ratecard"
import React from "react"
import { TextInput } from "components/inputs/TextInput"
import { fetchMediaTypes } from "api/globalMediaTypes"
import { patchRateCard } from "api/ratecard"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router-dom"
import Options from "components/inputs/Options"
import { AsyncCombobox } from "components/inputs/AsyncCombobox"
import {
  fetchRateCardClientsOptions,
  fetchRateCardClientsOptionsQueryKey,
} from "components/inputs/Clients"

type Props = {
  data?: RateCardDetails
}

type Option = {
  value: string
  label: string
}

export default function RateCardProfile(props: Props) {
  const { data } = props
  const { t } = useI18n()
  const { hasAuthority } = useAuthenticated()
  const { id } = useParams<{ id: string }>()
  const [localState, setLocalState] = React.useState<Partial<RateCardDetails>>({
    effectiveDate: data?.effectiveDate,
    mediaOwnerId: data?.mediaOwnerId,
    clientId: data?.clientId,
    negotiationType: data?.negotiationType || "NEGOTIATED",
    loading: data?.loading,
  })

  const [errors, setErrors] = React.useState<ErrorsMap | undefined>()

  const negotiationOptions = NEGOTIATION_TYPES?.map((nt) => ({
    value: nt,
    label: t(`rate.card.negotiation.type._${nt}`),
  })) as Option[]

  const { data: mediaTypes } = useQuery(
    [
      "global-media-types",
      {
        configScope: "SUPPLIER" as MediaConfigScope,
        configOwnerId: data?.supplierId,
      },
    ],
    fetchMediaTypes,
    {
      enabled: !!data?.supplierId,
    },
  )

  // update rate card
  const [update] = useMutation(patchRateCard, {
    onSuccess: () => {
      queryCache.invalidateQueries(["rate-card-details", id])
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const mediaType = mediaTypes?.find((mt) => mt.id === data?.mediaTypeId)

  const handleDateChange = (date: Date | null) => {
    setLocalState((s) => ({ ...s, effectiveDate: date?.toISOString() }))
    setErrors((e) => ({ ...e, effectiveDate: "" }))
    data?.effectiveDate !== date?.toISOString &&
      update({ id, effectiveDate: date?.toISOString() })
  }

  const handleChange = (
    evt: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = evt?.target
    setLocalState((s) => ({ ...s, [name]: value }))
  }

  const handleAutoCompChange = (_: any, opt: Option | null) => {
    setLocalState((s) => ({ ...s, clientId: opt?.value }))
  }

  const handleBlur = (
    evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = evt?.target
    update({ id, [name]: value })
  }

  const handleAutoCompBlur = (
    evt: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name } = evt?.target
    update({ id, [name]: localState?.[name as keyof RateCardDetails] ?? null })
  }

  const handleSwitchChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setLocalState((s) => ({ ...s, loading: checked }))
    update({ id, loading: checked })
  }

  React.useEffect(
    () =>
      setLocalState({
        effectiveDate: data?.effectiveDate,
        mediaOwnerId: data?.mediaOwnerId,
        loading: data?.loading,
        negotiationType: data?.negotiationType || "NEGOTIATED",
        clientId: data?.clientId,
      }),
    [
      data?.effectiveDate,
      data?.loading,
      data?.mediaOwnerId,
      data?.negotiationType,
      data?.clientId,
    ],
  )

  return (
    <div className={styles.container}>
      <div>
        <div className={cx("BodyBody-1BlackBold", styles.title)}>
          {t("rate.card.details")}
        </div>
        <TextInput
          name="mediaType"
          label={t("rate.card.media.type")}
          value={mediaType?.name || ""}
          margin="normal"
          size="medium"
          fullWidth={true}
          disabled={true}
        />
        <DateInput
          name="effectiveDate"
          label={t("rate.card.effective.date")}
          margin="normal"
          size="medium"
          value={localState?.effectiveDate}
          onChange={handleDateChange}
          inputVariant="outlined"
          fullWidth={true}
          hasStartAdornment
          error={!!errors && !!errors["effectiveDate"]}
          helperText={!!errors ? t(errors["effectiveDate"]) : ""}
          disabled={!hasAuthority(["AUTH_EDIT_RATE_CARD"])}
        />
        <AsyncCombobox<Option>
          name="clientId"
          label={t("rate.card.client")}
          onChange={handleAutoCompChange}
          onBlur={handleAutoCompBlur}
          value={localState?.clientId}
          fetchOptions={fetchRateCardClientsOptions}
          queryKey={fetchRateCardClientsOptionsQueryKey}
          param={{ key: "supplierId", value: data?.supplierId ?? "" }}
          size="medium"
          margin="normal"
          classes={{ option: styles.option }}
          disabled={!hasAuthority(["AUTH_EDIT_RATE_CARD"])}
          disableQuery={!hasAuthority(["AUTH_LOOKUP_CLIENT"])}
        />
        <Options
          name="negotiationType"
          label={t("rate.card.negotiation.type")}
          options={negotiationOptions}
          value={localState?.negotiationType}
          onChange={handleChange}
          onBlur={handleBlur}
          margin="normal"
          size="medium"
          fullWidth
          disabled={!hasAuthority(["AUTH_EDIT_RATE_CARD"])}
        />
      </div>
      <div>
        <LoadingTable
          checked={localState?.loading || false}
          onSwitchChange={handleSwitchChange}
        />
      </div>
    </div>
  )
}

const contextOptions = React.createContext<
  { onDelete: any; onUpdate: any } | undefined
>(undefined)

export const useContextOptions = () => {
  const options = React.useContext(contextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  container: css`
    display: grid;
    grid-template-columns: 200px 700px;
    grid-column-gap: 100px;
    padding-top: var(--spacing-xs);
  `,
  title: css`
    display: flex;
    align-items: center;
    padding: var(--spacing-s) 0;
    padding-right: var(--spacing-xs);
  `,
  option: css`
    font-size: 13px;
  `,
}
